import get from 'lodash.get';
import map from 'lodash.map';
import React, { useState } from 'react';
import styled from 'styled-components/macro';
import Breakpoints from 'themes/constants/breakpoints';
import { useHistory } from 'react-router-dom';
import queryString from 'query-string';

// Styles
const Wrap = styled.div`
  display: flex;
  flex-flow: row nowrap;
  overflow: hidden;
  font-size: 16px;
  line-height: 1.2;
  font-weight: 700;
  width: 100%;
  max-width: 320px;
  margin: 0 auto;
  @media only screen and (${Breakpoints.TABLET}) {
    margin: 10px auto 25px;
    max-width: 280px;
    font-weight: 500;
  }
  @media only screen and (${Breakpoints.LAPTOP}) {
    margin: -20px 0 30px;
    max-width: 360px;
  }
`;
const TabsItem = styled.div<{ isActive?: boolean }>`
  position: relative;
  color: ${({ theme, isActive }) => theme.color.tabs.collections[isActive ? 'active' : 'inactive']};
  cursor: pointer;
  width: 50%;
  min-height: 30px;
  padding: 5px 15px 10px;
  text-align: center;
  user-select: none;
  &:after {
    content: "";
    position: absolute;
    left: 0;
    bottom: 0;
    right: 0;
    height: 6px;
    background-color: ${({ theme, isActive }) => theme.color.tabs.collections[isActive ? 'active' : 'inactive']};
  }
  &:first-child:after {
    border-radius: 100px 0 0 100px;
  }
  &:last-child:after {
    border-radius: 0 100px 100px 0;
  }
`;

// Interfaces
export interface ITabsCollectionsItem {
  value: string,
  name: string
}
export interface ITabsCollections {
  tabs: ITabsCollectionsItem[]
  className?: string
  withCurrentQueries?: boolean
  query?: string
  routeTrigger?: RouteTrigger
}
export enum RouteTrigger {
  QUERY = 'QUERY',
  PATHNAME = 'PATHNAME',
}

// Component
const TabsCollections = (props: ITabsCollections) => {
  const {
    tabs,
    className,
    query = 'tab',
    routeTrigger = RouteTrigger.QUERY,
    withCurrentQueries = false,
  } = props;

  const history = useHistory();
  const search = history.location.search || '';
  const pathname = history.location.pathname || '';
  const queries = queryString.parse(search);
  const tabQuery = queries?.[query] || '';
  const hasQueryInTabs = tabs.find((item: ITabsCollectionsItem) => item.value === tabQuery);
  const hasPathInTabs = tabs.find((item: ITabsCollectionsItem) => item.value === pathname);

  const isPathnameTrigger = routeTrigger === RouteTrigger.PATHNAME;

  const firstTabValue = get(tabs, [0, 'value']);
  const initCurrent = isPathnameTrigger
    ? (hasPathInTabs ? pathname : firstTabValue)
    : (hasQueryInTabs ? tabQuery : firstTabValue);

  const [current, setCurrent] = useState(initCurrent);

  return (
    <Wrap className={className}>
      {map(tabs, (item: ITabsCollectionsItem) => {
        const { name, value } = item;
        const handleClick = () => {
          const queryWithNewValue = {
            ...queries,
            [query]: value,
          };
          setCurrent(value);
          if (isPathnameTrigger) {
            if (withCurrentQueries) {
              history.push({ pathname: value, search });
            } else {
              history.push({ pathname: value });
            }
          } else {
            history.push({ search: queryString.stringify(queryWithNewValue) });
          }
        };

        return (
          <TabsItem
            onClick={handleClick}
            isActive={value === current}
            key={value}
          >
            {name}
          </TabsItem>
        );
      })}
    </Wrap>
  );
};

export default TabsCollections;
